import React, { forwardRef } from 'react';

import { CutIcon } from 'src/components/icons/CutIcon';
import { useEditorTabStateSelector } from 'src/editor/editor-tab-state-registry';
import { BaseMenuOption } from 'src/editor/toolbars/menus/BaseMenuOption';
import type { CommonMenuItemProps } from 'src/editor/toolbars/menus/types';
import { ClientTransforms } from 'src/editor/transforms';
import { useCurrentUserId } from 'src/gql';
import { useDocumentParams } from 'src/helpers';
import { IS_APPLE } from 'src/helpers/environment';
import { useAuthorizedTransforms } from 'src/hooks';
import { useEditor } from 'src/layout-engine';

/**
 * @deprecated Once the context menu is using DeclarativeMenu, remove this and refactor out the
 * relevant menu item configs from useEditMenu instead.
 */
export const CutMenuItem = forwardRef(function CutMenuItem(
  props: CommonMenuItemProps,
  ref: React.Ref<HTMLLIElement>
): JSX.Element {
  const { closeRootMenu, ...rest } = props;

  const editor = useEditor();
  const { documentId } = useDocumentParams();
  const currentUserId = useCurrentUserId();
  const { AuthorizedTransforms } = useAuthorizedTransforms();
  const editingMode = useEditorTabStateSelector(state => state.documentUi.editingMode);

  const shortCut = IS_APPLE ? '⌘X' : 'Ctrl+X';

  async function cut(): Promise<void> {
    closeRootMenu();

    if (!currentUserId || !editingMode) return;

    const isCopied = await ClientTransforms.copyToClipboard(editor, { documentId });
    if (isCopied) {
      AuthorizedTransforms.deleteContent(editor, {
        authorId: currentUserId,
        direction: 'backward',
        editingMode,
      });
    }
    ClientTransforms.focus(editor);
  }

  return (
    <BaseMenuOption
      ref={ref}
      icon={<CutIcon />}
      label="Cut"
      shortCutKey={shortCut}
      onClick={cut}
      {...rest}
    />
  );
});
